import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Box, Stack, Card, Typography, Tooltip, SxProps } from '@mui/material';
import { DragIndicator, InfoOutlined } from '@mui/icons-material';
import { useFeatures } from 'components/FeaturesContext';
import { LessonSection } from 'controllers/types';
import {
  useDraggableList,
  getDraggableStyles,
  getDraggableProps,
} from 'components/admin/LessonBuilder/useDraggableList';
import { LessonsLoadingIndicator } from 'components/admin/LessonBuilder/LessonsLoadingIndicator';
import { useUpdateSectionCustom } from 'controllers/react-query';
import { AddSectionButton } from './AddSectionButton';

function OutlineItem({ section, isDraggable }: { section: LessonSection; isDraggable: boolean }) {
  const { question, wait, explanation } = section;
  const isMissingParameter =
    (question && question.description === '') ||
    (wait && wait.description === '') ||
    (explanation && explanation.description === '');
  return (
    <Stack direction='row' alignItems='center' spacing={2}>
      {isDraggable && <DragIndicator />}
      <Typography variant='labelLarge'>{section.topic}</Typography>
      {isMissingParameter && (
        <Tooltip title='Section is missing required field'>
          <InfoOutlined />
        </Tooltip>
      )}
    </Stack>
  );
}

type SectionListProps = {
  selectedIdx: number;
  selectIdx: (idx: number) => void;
  sx?: SxProps;
  lessonSections: LessonSection[];
  isLoading: boolean;
};
export function SectionList({ selectedIdx, selectIdx, isLoading, sx, lessonSections }: SectionListProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { lessonId } = useParams<{ lessonId: string }>();
  const { enable_section_editing: enableSectionEditing = false } = useFeatures(); // TODO(derick): remove feature flag enableSectionEditing once all Create/Re-order/Delete actions are merged
  const { mutate, isPending } = useUpdateSectionCustom(Number(lessonId));

  const [sectionItems, setSectionItems] = useState(lessonSections);
  useEffect(() => {
    if (lessonSections.length === 0) return;
    setSectionItems(lessonSections); // set items when items is loaded
  }, [lessonSections]);

  const draggableListResults = useDraggableList<LessonSection>(setSectionItems, ({ id, topic, position }, newIndex) => {
    const newPosition = newIndex + 1; // positions are 1-indexed
    if (position === newPosition) return;
    mutate(
      { id, topic, position: newPosition },
      {
        onSuccess: () => {
          selectIdx(newIndex);
        },
        onError: (err: Error) => {
          const errorTitle = `Failed to update section: ${topic}`;
          console.error(errorTitle, id, err);
          enqueueSnackbar(errorTitle, { variant: 'error' });
          setSectionItems(lessonSections); // reset items to original order
        },
      },
    );
  });

  return (
    <Stack gap={1} p={2} overflow='auto' sx={sx}>
      {isLoading || isPending ? (
        <LessonsLoadingIndicator />
      ) : (
        sectionItems.map((section, idx) => {
          const isDraggableIndex = enableSectionEditing && idx !== lessonSections.length - 1; // Final section is not draggable
          return (
            <Card
              key={section.id}
              onClick={() => selectIdx(idx)}
              onMouseDown={() => selectIdx(idx)}
              sx={{
                cursor: 'pointer',
                bgcolor: selectedIdx === idx ? 'secondaryContainer.main' : 'transparent',
                p: 1,
                pl: 2,
                flexShrink: 0,
                ...(isDraggableIndex ? getDraggableStyles(idx, draggableListResults) : {}),
              }}
              {...(isDraggableIndex ? getDraggableProps(idx, draggableListResults) : {})}
            >
              <OutlineItem section={section} isDraggable={isDraggableIndex} />
            </Card>
          );
        })
      )}
      {enableSectionEditing ? (
        <Box>
          <AddSectionButton onCreateSection={() => selectIdx(lessonSections.length - 1)} disabled={isLoading} />
        </Box>
      ) : null}
    </Stack>
  );
}
